import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { invalidateHomePersonalization } from '../../pages/home/homeRefresh';
const body = (r) => r.data?.data ?? r.data;
export function useTrackFeedbackRating(item, active, onSaved) {
  const userId = useUserStore((s) => s.user?.id),
    client = useQueryClient();
  const [draft, setDraft] = useState(undefined),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const lock = useRef(false),
    epoch = useRef(0),
    token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
  useEffect(() => {
    const generation = ++epoch.current;
    setDraft(undefined);
    setError(false);
    setBusy(false);
    lock.current = false;
    return () => {
      epoch.current = generation + 1;
    };
  }, [active, item.id, userId, token]);
  const key = ['homeTrackRating', userId, item.id];
  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const result = body(
        await api.getAuth(`/tracks/${item.id}/rating`, { signal, suppressGlobalError: true }),
      );
      if (Number(result?.trackId) !== Number(item.id))
        throw new Error('Unexpected rating identity');
      return result;
    },
    enabled: Boolean(active && userId),
    retry: false,
    staleTime: 0,
  });
  const initial = query.data?.userRating ?? null,
    value = draft === undefined ? initial : draft,
    dirty = active && draft !== undefined && value !== initial;
  const save = async () => {
    if (!active || !userId || lock.current || query.isPending || query.isError) return;
    const generation = epoch.current;
    const current = () =>
      epoch.current === generation &&
      useUserStore.getState().user?.id === userId &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === token;
    if (!current()) return;
    lock.current = true;
    setBusy(true);
    setError(false);
    try {
      const response =
        value == null
          ? await api.deleteAuth(`/tracks/${item.id}/rating`, { suppressGlobalError: true })
          : await api.putAuth(
              `/tracks/${item.id}/rating`,
              { rating: value },
              { suppressGlobalError: true },
            );
      if (!current()) return;
      const summary = body(response);
      if (Number(summary?.trackId) !== Number(item.id))
        throw new Error('Unexpected rating identity');
      client.setQueryData(key, summary);
      setDraft(undefined);
      onSaved(summary.userRating == null);
      void invalidateHomePersonalization(client);
      for (const family of [
        'albumDetail',
        'me',
        'myTrackRatings',
        'myMusicRecords',
        'publicMusicRecords',
        'userContent',
        'search',
      ])
        void client.invalidateQueries({ queryKey: [family] });
    } catch {
      if (current()) setError(true);
    } finally {
      if (current()) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  return { query, value, dirty, busy, error, select: setDraft, save };
}
